import React, { useEffect, useState } from "react";
import { api, money, money0 } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { KpiCard } from "../components/UI";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

const TIPS = {
  weekly: "Sum of the current weekly rate on every active hire right now. Rate changes take effect from their effective date. Stand-down days aren't subtracted here — they only reduce actual monthly billing.",
  monthly: "Weekly earning × 4.33 — the same maths month-end uses when a hire runs a full month with no stand-downs or rate changes.",
  fleet: "How many machines are currently out on hire vs. the total number of machines across all six owning companies. Quote cranes (quote-only, not on the fleet) are NOT counted here.",
  hires: "Number of hire records with status 'active' — machines currently out with a customer.",
  fleet_by_company: "Fleet split by owning company. 'Out earning' = status on-hire, 'Free' = status free. Sale-pending machines aren't broken out yet.",
  top_owing: "Top customers by 'owed to us' balance, largest first. Balances come from approved month-end runs that haven't been marked paid.",
  recent_months: "Most recent month-end runs — draft or approved. 'Total' is the sum of all charge lines in that month.",
};

function Info({ text }) {
  return (
    <span data-testid="report-tip" style={{
      display:"inline-flex", alignItems:"center", justifyContent:"center",
      width:16, height:16, borderRadius:"50%", background:"var(--ach-page-bg)",
      color:"var(--ach-text-2)", fontSize:10, marginLeft:6, cursor:"help",
      border:"1px solid var(--ach-card-border)"
    }} title={text}>?</span>
  );
}

function toCSV(rows) {
  if (!rows || rows.length === 0) return "";
  const headers = Object.keys(rows[0]);
  const escape = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  return [headers.join(","), ...rows.map(r => headers.map(h => escape(r[h])).join(","))].join("\n");
}

function downloadCSV(name, csv) {
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(()=>URL.revokeObjectURL(url), 1000);
}

export default function Reports() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports").then(r => setData(r.data)); }, []);
  if (!data) return <div style={{padding: 40, color:"var(--ach-text-2)"}}>Loading…</div>;

  const totalCount = data.fleet_by_company.reduce((s, c) => s + c.count, 0);
  const totalOnHire = data.fleet_by_company.reduce((s, c) => s + c.on_hire, 0);

  const exportAll = () => {
    const kpiRows = [
      { section: "kpi", label: "Weekly run rate", value: data.weekly_run_rate },
      { section: "kpi", label: "Monthly run rate", value: data.monthly_run_rate },
      { section: "kpi", label: "Fleet out earning / total", value: `${totalOnHire}/${totalCount}` },
      { section: "kpi", label: "Active hires today", value: data.active_hire_count },
    ];
    const fleetRows = data.fleet_by_company.map(c => ({ section: "fleet", company: c.company, machines: c.count, on_hire: c.on_hire, free: c.free }));
    const owingRows = data.top_owing.map(c => ({ section: "top_owing", customer: c.legal_name, owed: c.owing_amount }));
    const monthRows = data.recent_months.map(r => ({ section: "month_end", month: r.month, status: r.status, lines: r.line_count, total: r.total }));
    const merged = [
      "section,label,value",
      ...kpiRows.map(r => `${r.section},"${r.label}",${r.value}`),
      "",
      "section,company,machines,on_hire,free",
      ...fleetRows.map(r => `${r.section},"${r.company}",${r.machines},${r.on_hire},${r.free}`),
      "",
      "section,customer,owed",
      ...owingRows.map(r => `${r.section},"${r.customer}",${r.owed}`),
      "",
      "section,month,status,lines,total",
      ...monthRows.map(r => `${r.section},${r.month},${r.status},${r.lines},${r.total}`),
    ].join("\n");
    const stamp = new Date().toISOString().slice(0,10);
    downloadCSV(`ach-reports-${stamp}.csv`, merged);
  };

  const printPage = () => window.print();

  return (
    <div data-testid="reports-page">
      <PageHeader
        title="Reports"
        subtitle="Plain-english numbers about the business — every table has a ? next to the heading explaining how it&apos;s worked out."
        actions={
          <>
            <button className="ach-btn ach-btn--grey" onClick={exportAll} data-testid="rep-export-csv">Export CSV</button>
            <button className="ach-btn ach-btn--grey" onClick={printPage} data-testid="rep-print">Print / save PDF</button>
          </>
        }
      />

      <div className="ach-kpi-row" style={{gridTemplateColumns:"repeat(4, minmax(0,1fr))"}}>
        <div data-testid="rep-kpi-weekly-wrap" title={TIPS.weekly}>
          <KpiCard tone="green" number={money0(data.weekly_run_rate)} label={<span>Earning per week right now<Info text={TIPS.weekly} /></span>} testid="rep-kpi-weekly" />
        </div>
        <div data-testid="rep-kpi-monthly-wrap" title={TIPS.monthly}>
          <KpiCard tone="maroon" number={money0(data.monthly_run_rate)} label={<span>Which is per month<Info text={TIPS.monthly} /></span>} testid="rep-kpi-monthly" />
        </div>
        <div data-testid="rep-kpi-fleet-wrap" title={TIPS.fleet}>
          <KpiCard tone="neutral" number={`${totalOnHire}/${totalCount}`} label={<span>Of the fleet out earning<Info text={TIPS.fleet} /></span>} testid="rep-kpi-fleet" />
        </div>
        <div data-testid="rep-kpi-hires-wrap" title={TIPS.hires}>
          <KpiCard tone="blue" number={data.active_hire_count} label={<span>Active hires today<Info text={TIPS.hires} /></span>} testid="rep-kpi-hires" />
        </div>
      </div>

      <div style={{display:"grid", gridTemplateColumns:"1fr 1fr", gap: 16}}>
        <div className="ach-card">
          <h2 className="ach-card__title">Fleet by owning company<Info text={TIPS.fleet_by_company} /></h2>
          <table className="ach-table">
            <thead>
              <tr>
                <th>Company</th>
                <th>Machines</th>
                <th>Out earning</th>
                <th>Free</th>
              </tr>
            </thead>
            <tbody>
              {data.fleet_by_company.map(c => (
                <tr key={c.company}>
                  <td>{c.company}</td>
                  <td>{c.count}</td>
                  <td style={{color:"var(--ach-green)", fontWeight: 600}}>{c.on_hire}</td>
                  <td>{c.free}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ach-card">
          <h2 className="ach-card__title">Top customers by money owed<Info text={TIPS.top_owing} /></h2>
          <table className="ach-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th className="money">Owed</th>
              </tr>
            </thead>
            <tbody>
              {data.top_owing.length === 0 ? (
                <tr><td colSpan={2} style={{textAlign:"center", padding: 24, color:"var(--ach-text-2)"}}>Nobody owes anything. Nice.</td></tr>
              ) : data.top_owing.map(c => (
                <tr key={c.legal_name}>
                  <td>{c.legal_name}</td>
                  <td className="money">{money(c.owing_amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="ach-card" style={{marginTop: 16}}>
        <h2 className="ach-card__title">Last few month-end runs<Info text={TIPS.recent_months} /></h2>
        <table className="ach-table">
          <thead>
            <tr>
              <th>Month</th>
              <th>Status</th>
              <th>Lines</th>
              <th className="money">Total</th>
            </tr>
          </thead>
          <tbody>
            {data.recent_months.length === 0 ? (
              <tr><td colSpan={4} style={{textAlign:"center", padding: 24, color:"var(--ach-text-2)"}}>No month-end has been generated yet.</td></tr>
            ) : data.recent_months.map(r => {
              const [y, m] = r.month.split("-");
              return (
                <tr key={r.month}>
                  <td>{MONTHS[Number(m) - 1]} {y}</td>
                  <td>{r.status}</td>
                  <td>{r.line_count}</td>
                  <td className="money">{money(r.total)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
